import React, { useState, useEffect } from 'react';
import { Calendar, Edit3, Save, X, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';
import { schoolDataApi } from '../../services/api';
import { ConfirmDialog } from '../ui';

const YEAR_FORMAT = /^\d{4}-\d{4}$/;

const AcademicYearSettings: React.FC = () => {
  const [academicYear, setAcademicYear] = useState('');
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [draftYear, setDraftYear] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    loadAcademicYear();
  }, []);

  const loadAcademicYear = async () => {
    try {
      setLoading(true);
      const year = await schoolDataApi.settings.getAcademicYear();
      setAcademicYear(year);
    } catch (error) {
      toast.error('Failed to load academic year');
      console.error('Error loading academic year:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = () => {
    setDraftYear(academicYear);
    setIsEditing(true);
  };

  const handleRequestSave = () => {
    if (!YEAR_FORMAT.test(draftYear)) {
      toast.error('Academic year must be in format YYYY-YYYY, e.g. 2027-2028');
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmSave = async () => {
    try {
      setSaving(true);
      await schoolDataApi.settings.setAcademicYear(draftYear);
      setAcademicYear(draftYear);
      setIsEditing(false);
      setConfirmOpen(false);
      toast.success('Academic year updated');
    } catch (error) {
      toast.error('Failed to update academic year');
      console.error('Error updating academic year:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#e2916d] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-[#f3ede2]">Academic Year</h3>
        <p className="text-[#b9ad9b] text-sm mt-1">
          The single current session used across fee structures, results, and bills school-wide.
        </p>
      </div>

      <div className="bg-[#28221a] rounded-xl border border-[#3a3122] p-6 max-w-md">
        {isEditing ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">New Academic Year</label>
              <input
                type="text"
                value={draftYear}
                onChange={(e) => setDraftYear(e.target.value)}
                placeholder="2027-2028"
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setIsEditing(false)}
                className="flex items-center gap-2 px-4 py-2 text-[#b9ad9b] border border-[#3a3122] rounded-lg hover:bg-[#211c15] transition-colors"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
              <button
                onClick={handleRequestSave}
                className="flex items-center gap-2 px-4 py-2 bg-[#e2916d] hover:bg-[#f0a67d] text-white rounded-lg transition-colors"
              >
                <Save className="w-4 h-4" />
                Save
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-[#332217] rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-[#e2916d]" />
              </div>
              <div>
                <p className="text-xs text-[#8a7f6e]">Current Academic Year</p>
                <p className="text-xl font-bold text-[#f3ede2]">{academicYear}</p>
              </div>
            </div>
            <button
              onClick={handleStartEdit}
              className="p-2 text-[#8a7f6e] hover:text-[#e2916d] hover:bg-[#211c15] rounded-lg transition-colors"
              title="Change academic year"
            >
              <Edit3 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <div className="flex items-start gap-2 text-sm text-[#8a7f6e] max-w-md">
        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#f0b64c]" />
        <p>
          Changing this affects every new fee structure and result recorded from now on.
          Existing records keep the academic year they were created with.
        </p>
      </div>

      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmSave}
        title="Update Academic Year"
        message={`Set the school's current academic year to "${draftYear}"? This will apply to every new fee structure and result created from now on.`}
        type="warning"
        confirmText={saving ? 'Saving...' : 'Yes, Update'}
        cancelText="Cancel"
      />
    </div>
  );
};

export default AcademicYearSettings;
